import { TeamPlayer } from "../../types/entities/team-player";
import { activeMatchRoles } from "../../types/enums/match-role";
import { PlayerStatsWrapper } from "../../types/stats/player-stats";

export function getOrderedSquad(teamPlayers: TeamPlayer[]): TeamPlayer[] {
  return (
    teamPlayers
      ?.slice()
      .sort(
        (a: TeamPlayer, b: TeamPlayer) => a.battingOrder - b.battingOrder
      ) || []
  );
}

export function getNextPlayer(
  teamPlayers: TeamPlayer[],
  currentPlayer: string
): string {
  const squadInOrder = getOrderedSquad(teamPlayers);
  const currentPlayerIndex = squadInOrder.findIndex(
    (tp) => tp.playerId.value === currentPlayer
  );
  return squadInOrder[currentPlayerIndex + 1]?.playerId.value || null;
}

export function getPreviousPlayer(
  teamPlayers: TeamPlayer[],
  currentPlayer: string
): string {
  const squadInOrder = getOrderedSquad(teamPlayers);
  const currentPlayerIndex = squadInOrder.findIndex(
    (tp) => tp.playerId.value === currentPlayer
  );
  return squadInOrder[currentPlayerIndex - 1]?.playerId.value || null;
}

export function getCurrentPlayerIndex(
  squadInOrder: TeamPlayer[],
  currentPlayer: string
): number {
  return squadInOrder?.findIndex((tp) => tp.playerId.value === currentPlayer);
}

export function cannotGoLeft(
  teamPlayers: TeamPlayer[],
  currentPlayer: string
): boolean {
  const squadInOrder = getOrderedSquad(teamPlayers);
  const currentPlayerIndex = getCurrentPlayerIndex(squadInOrder, currentPlayer);
  return (
    currentPlayerIndex <= 0 ||
    !activeMatchRoles.find(
      (role) => role === squadInOrder[currentPlayerIndex - 1]?.matchRole
    )
  );
}

export function cannotGoRight(
  teamPlayers: TeamPlayer[],
  currentPlayer: string
): boolean {
  const squadInOrder = getOrderedSquad(teamPlayers);
  const currentPlayerIndex = getCurrentPlayerIndex(squadInOrder, currentPlayer);
  return (
    currentPlayerIndex === -1 ||
    currentPlayerIndex === squadInOrder.length - 1 ||
    !activeMatchRoles.find(
      (role) => role === squadInOrder[currentPlayerIndex + 1]?.matchRole
    )
  );
}

export function batchUpdate(
  currentStats: PlayerStatsWrapper,
  comparedStats: PlayerStatsWrapper,
  updates: string[],
  onUpdate: (updatedStats: PlayerStatsWrapper) => void,
  index: number = -1
) {
  const clonedComparedStats = PlayerStatsWrapper.clone(
    comparedStats.playerStats
  );
  let updatedStats = currentStats.playerStats;
  updates.forEach((key) => {
    if (index !== -1) {
      const array = currentStats.playerStats[key] as Array<unknown>;
      const valueToReplace = clonedComparedStats[key][index];
      array.splice(index, 1, valueToReplace);
      updatedStats = {
        ...updatedStats,
        [key]: array,
      };
    } else {
      updatedStats = {
        ...updatedStats,
        [key]: clonedComparedStats[key],
      };
    }
  });

  onUpdate(
    new PlayerStatsWrapper(
      currentStats.entityId,
      currentStats.playerStatsId,
      currentStats.createdBy,
      currentStats.createdAt,
      currentStats.name,
      updatedStats,
      currentStats.matchType
    )
  );
}

export function batchUpdateAll(
  currentStatsMap: Map<string, PlayerStatsWrapper>,
  comparedStatsMap: Map<string, PlayerStatsWrapper>,
  updates: string[],
  onBatchUpdate: (batchUpdatedStats: Map<string, PlayerStatsWrapper>) => void
) {
  let batchUpdatedStats = new Map();

  currentStatsMap.forEach((currentStats, playerId) => {
    const comparedStats = comparedStatsMap.get(playerId);
    if (!comparedStats) {
      return;
    }

    let updatedStats = currentStats.playerStats;

    updates.forEach((key) => {
      updatedStats = {
        ...updatedStats,
        [key]: comparedStats.playerStats[key],
      };
    });

    const updatedPlayerStatsWrapper = new PlayerStatsWrapper(
      currentStats.entityId,
      currentStats.playerStatsId,
      currentStats.createdBy,
      currentStats.createdAt,
      currentStats.name,
      updatedStats,
      currentStats.matchType
    );

    batchUpdatedStats.set(playerId, updatedPlayerStatsWrapper);
  });

  onBatchUpdate(batchUpdatedStats);
}
